import queryString from 'querystring';

import React, { useMemo } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import memoize from 'lodash.memoize';
import { bindActionCreators } from 'redux';

import { ThunkActionCreator } from '@ttstr/actions';
import { Article, Articles, Cart, Product, Variant } from '@ttstr/api';

export const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
export const BIC_PATTERN = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
export const IBAN_PATTERN = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/;

export type Uncasted<T> = {
  [key in keyof T]?: any;
};

export const filter = {
  keepAll: () => true,
  byKey: <T extends any>(key: string) => {
    const keyParts = key.split('.');
    return (item: T) => !!keyParts.reduce((i: any, k) => i && i[k], item);
  },
  byFulltextMatch(needle: string) {
    const lowerCaseNeedle = needle.trim().toLowerCase();
    const matcher = (item: any): boolean => {
      if (item === undefined) return false;
      if (item === null) return false;
      if (typeof item === 'string') {
        return item.trim().toLowerCase().includes(lowerCaseNeedle);
      }
      if (Array.isArray(item)) return item.some((i) => matcher(i));
      if (typeof item === 'object') {
        return Object.values(item).some((value) => matcher(value));
      }
      return false;
    };
    return matcher;
  },
};

export const sort = {
  byCallback: <T = any>(cb: (x: T) => any, reverse = false) => {
    const inverter = reverse ? -1 : +1;
    return (a: T, b: T) => {
      const aKey = cb(a);
      const bKey = cb(b);
      if (aKey > bKey) return +1 * inverter;
      if (aKey < bKey) return -1 * inverter;
      return 0;
    };
  },
  byKey: <T = any>(key: string, reverse = false) => {
    const keyParts = key.split('.');
    const inverter = reverse ? -1 : +1;
    return (a: T, b: T) => {
      const aKey = keyParts.reduce((i: any, k) => i && i[k], a);
      const bKey = keyParts.reduce((i: any, k) => i && i[k], b);
      if (aKey > bKey) return +1 * inverter;
      if (aKey < bKey) return -1 * inverter;
      return 0;
    };
  },
};

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Tool to create an n-length array
 * @param times Number of array elements
 * @returns An 1-based iteration of the elements
 */
export const repeat = (times: number) => Array.from(Array(times)).map((_, i) => i + 1);

export const productDetailLink = (product: Article, { canonicalUrl = '' } = {}) => {
  const slug = product.ptitle.replace('-wbr-', '') ?? '';
  if (canonicalUrl) return `${canonicalUrl}/product/${product.id}/${slug}`;
  return `/product/${product.id}/${slug}`;
};

export const getDateRangeForProducts = memoize((products: Articles) => {
  if (!products.length) return null;
  return products.reduce(
    ({ min, max }, product) => {
      if (!product.valid_start_on) return { min, max };
      const current = new Date(product.valid_start_on);
      return {
        min: min > current ? current : min,
        max: max < current ? current : max,
      };
    },
    { min: new Date(), max: new Date() }
  );
});

export function isNumeric(value: any) {
  return !Number.isNaN(Number(value)) && Number.isFinite(value);
}

export function leftMerge<T extends { [key: string]: any }>(left: T, ...rest: Partial<T>[]): T {
  if (!left) return left;
  if (!rest || !rest.length) return left;
  const reverseRest = rest.filter(Boolean).reverse();
  return Object.entries(left).reduce((acc, [key, value]) => {
    const foundItem = reverseRest.find((candidate) => {
      return key in candidate && candidate[key] != null;
    });
    const mergedValue = foundItem ? foundItem[key] : value;

    if (value instanceof Object && mergedValue instanceof Object) {
      return {
        ...acc,
        [key]: leftMerge(value, mergedValue),
      };
    }

    return {
      ...acc,
      [key]: mergedValue,
    };
  }, {} as T);
}

/**
 * @see https://react-redux.js.org/next/api/hooks#recipe-useshallowequalselector
 * @param selector
 */
export function useShallowEqualSelector<TState, TSelected>(selector: (state: TState) => TSelected) {
  return useSelector<TState, Readonly<TSelected>>(selector, shallowEqual);
}

/**
 *
 * @param actions key-value pair of actions where the key is the name of the action and the value the actual function
 * @param dependencies optional list of dependencies that'll trigger React.useMemo to rerender
 */
export function useActions<T extends { [key: string]: ThunkActionCreator }>(actions: T, dependencies?: any[]): any {
  const dispatch = useDispatch();
  return useMemo(() => bindActionCreators(actions, dispatch), dependencies ? [dispatch, ...dependencies] : [dispatch]);
}

/**
 * Helper that returns a ref indicating if the current component is still mounted
 */
export function useMounted() {
  const isMounted = React.useRef(false);
  React.useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);
  return isMounted;
}

type CallbackRef<T> = (instance: T) => void;

export function useCombinedRefs<T extends HTMLElement, A extends (React.MutableRefObject<T> | CallbackRef<T>)[]>(
  ...refs: A
): CallbackRef<T> {
  return React.useCallback((ref: T) => {
    refs.forEach((r) => {
      if (typeof r === 'function') (r as any)(ref);
      else if (r !== null && typeof r === 'object' && 'current' in r) r.current = ref;
    });
  }, refs);
}

export function getDefaults<T extends { [key: string]: any }>(src: Partial<T>, defaults: T, prefix = ''): T {
  return Object.keys(defaults).reduce((acc, key) => {
    const path = prefix + key;
    if (!src) return acc;
    if (src[key] == null) return acc;
    if (src[key] === '') return acc;
    if (src[key] === defaults[key]) return acc;
    if (typeof defaults[key] === 'object' && typeof src[key] === 'object') {
      const sub = getDefaults(src[key], defaults[key]);
      if (Object.keys(defaults).length) {
        return {
          ...acc,
          [path]: sub,
        };
      }
      return acc;
    }
    return {
      ...acc,
      [path]: src[key],
    };
  }, {} as T);
}

export function useQueryString() {
  const location = useLocation();

  const qs = React.useMemo(() => {
    return queryString.parse(location.search.substring(1));
  }, [location.search]);

  return qs;
}

export function useToggle(initial = false): [boolean, () => void, React.Dispatch<React.SetStateAction<boolean>>] {
  const [state, setState] = React.useState(initial);

  const toggleState = () =>
    setState((previous) => {
      return !previous;
    });
  return [state, toggleState, setState];
}

/**
 * beware: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/test
 * > as with exec() (or in combination with it), test() called multiple times on the same global regular expression instance will advance past the previous match.
 * > As long as test() returns true, lastIndex will not reset—even when testing a different string!
 *
 * e.g. don't use the global modifier.
 */

const ABSOLUTE_URL_MATCHER = /^(([a-z+_-]+:)|\/\/)/i;

/**
 * Returns true if the URL is absolute
 *
 * Example absolute URLs:
 * * `mailto:test@absolute.url`
 * * `https://domain.tld/path/to/file.ext`
 * * `//keep-protocol.tld`
 *
 * Example relative URLs (which will not match):
 * * `/absolute-path-in-the-same-domain/file.ext`
 * * `relative/path/in/the/same/domain?query=string`
 *
 * @param url string to test
 * @see ABSOLUTE_URL_MATCHER const for more details
 */
export function isAbsoluteURL(url: string) {
  return ABSOLUTE_URL_MATCHER.test(url);
}


/**
 * Shop Titles
 * @important These values must match the "application-name" meta tag in the respective shops which is set in shop-config.json
 */
export const LOBRECHT = 'FELIX LOBRECHT - Tickets und Merchandise';
export const KUS = 'Kopf & Steine Shop';
export const APRIL_ART = 'APRIL ART - Merchandise & Records';
export const PAULA_HARTMANN = 'PAULA HARTMANN - Tickets & Merchandise';
export const VANESSA_MAI = 'VANESSA MAI';
export const MORD_AUF_X = 'MORD AUF EX - Tickets & Merchandise';
export const FRAU_GRETEL = 'Frau Gretel Shop';


/**
 * Event ID Keys for deduplication on the server side if CAPI is used
 */
export const PURCHASE_ID_KEY = 'purchaseEventId';
export const ATC_ID_KEY = 'addToCartEventId';
export const VIEW_CONTENT_ID_KEY = 'viewContentEventId';
export const INIT_CHECKOUT_ID_KEY = 'initCheckoutEventId';

export function trackMetaAndTiktokEvent(eventName: string, eventData: any, specialEventIdName?: string, customEventId?: string) {
  if("ttq" in window){
    window.ttq.track(eventName, eventData);
  }
  if("fbq" in window){
    const eventId = customEventId || crypto.randomUUID();
    sessionStorage.setItem(specialEventIdName || "eventId", eventId);
    window.fbq('track', eventName, eventData, {event_id: eventId});
  }
}

export function trackAddToCartEvent(
  variant: Variant,
  count: number,
  product?: Product,
  customEventId?: string
) {
  if (!variant) return;
  if("fbq" in window || "ttq" in window) {
    const shopsArray = [
      KUS,
      LOBRECHT,
      APRIL_ART,
      VANESSA_MAI,
      MORD_AUF_X,
      FRAU_GRETEL
    ];
    const title = document.querySelector('meta[name="application-name"]')?.content;
    if(title && shopsArray.includes(title)){
      const eventName = 'AddToCart';
      const defaultCurrency = 'EUR';
      const totalPrice = Number(variant.total_price || variant.price) * count;
      const contentType = 'product';
      const desc =
        product?.title ||
        variant.full_title ||
        variant.title ||
        product?.description ||
        variant.article?.full_title ||
        variant.article?.title ||
        product?.ptitle;
      const contentIds = [variant.id || product?.id || variant.article_id];
      const contentsElement = [
        {
          id: variant.id || product?.id || variant.article_id,
          quantity: count,
          item_price: Number(variant.total_price || variant.price),
        }];
      const eventData = {
        content_type: contentType,
        num_items: count,
        value: totalPrice,
        currency: defaultCurrency,
        content_ids: contentIds,
        contents: contentsElement,
      };
      if(desc){
        eventData['content_name'] = desc;
      }
      trackMetaAndTiktokEvent(eventName, eventData, ATC_ID_KEY, customEventId);
    }
  }
}

export function trackViewContentEvent(product: any, customEventId?: string){
  if("fbq" in window || "ttq" in window) {
    const title = document.querySelector('meta[name="application-name"]')?.content;
    const shopsArray = [KUS, LOBRECHT, FRAU_GRETEL];
    if(title && shopsArray.includes(title)){
      const eventName = 'ViewContent';
      const price = product.min_price || product.max_price || 0;
      const contentType = 'product';
      const contentName = product.title || product.ptitle || '';
      const contentIds = [product.id];
      const defaultCurrency = 'EUR';

      const contentsElement = [
        {
          id: product.id,
          quantity: 1,
          item_price: price,
        },
      ]
      const eventData = {
        content_type: contentType,
        content_name: contentName,
        content_ids: contentIds,
        contents: contentsElement,
        value: price,
        currency: defaultCurrency,
      };
      trackMetaAndTiktokEvent(eventName, eventData, VIEW_CONTENT_ID_KEY, customEventId);
    }
  }

}

export function trackPurchaseEvent(cart: Cart, customEventId?: string){
  if("fbq" in window || "ttq" in window) {
    const shopTitle = document.querySelector('meta[name="application-name"]')?.content;
    const shopsArray = [KUS, PAULA_HARTMANN, LOBRECHT, VANESSA_MAI, MORD_AUF_X, APRIL_ART, FRAU_GRETEL];
    if (cart && shopTitle && shopsArray.includes(shopTitle)) {
      const eventName = 'Purchase';
      const contentsArray = [];
      const price = cart.price_after_coupons || cart.total_value || 0;
      const contentType = 'product';
      for (const item of cart.items || []) {
        if (item.variant.is_fee) continue;
        if (item.variant?.article?.id) {
          contentsArray.push({
            id: item.variant.article.id,
            quantity: item.quantity,
            item_price: item.total_price / item.quantity,
          });
        }
      }
      const contentIds = contentsArray.map(c => c.id);
      const eventData = {
        contents: contentsArray,
        value: price,
        currency: 'EUR',
        content_type: contentType,
        content_ids: [...contentIds],
      };
      trackMetaAndTiktokEvent(eventName, eventData, PURCHASE_ID_KEY, customEventId);
    }
  }
}

export function trackInitiateCheckoutEvent(cart: Cart, customEventId?: string){
  if("fbq" in window || "ttq" in window) {
    const shopTitle = document.querySelector('meta[name="application-name"]')?.content;
    const shopsArray = [LOBRECHT, KUS, VANESSA_MAI, MORD_AUF_X, APRIL_ART, FRAU_GRETEL]
    if (cart && shopsArray.includes(shopTitle)) {
      const eventName = 'InitiateCheckout';
      const contentsArray = [];
      for (const item of cart.items || []) {
        if (item.variant.is_fee || item.quantity === 0) continue;
        contentsArray.push({
          quantity: item.quantity,
          id: item.variant.article.id,
          item_price: item.total_price / item.quantity,
        })
      }
      const contentIds = contentsArray.map(c => c.id);
      const eventData = {
        contents: contentsArray,
        value: cart.price_after_coupons || cart.total_value || 0,
        currency: 'EUR',
        content_type: 'product',
        content_ids: [...contentIds],
      }
      trackMetaAndTiktokEvent(eventName, eventData, INIT_CHECKOUT_ID_KEY, customEventId);
    }
  }
}

export function getPpClientId(clientId: any) {
  return (process.env.NODE_ENV === 'development') ? "BAAugznqYbyxne-41twQLBnj7EVgVlHhWposK5ygpFvSD2_QLS1j65r4CJipjl0q2FqqmEHiNxqKcBKDKw" : clientId;
}
export function getPpBnCode(bnCode: any) {
  return (process.env.NODE_ENV === 'development') ? "tickettoaster_Cart_PPCP" : bnCode;
}


const OnAcceptedTargeting = "CookieScriptCategory-targeting"
const META_DEVELOPMENT_PIXEL = "946706940439084"
const TIKTOK_DEVELOPMENT_PIXEL = "D2QL063C77UALVVH1EOG"

/**
 * ### Inject Meta Pixels
 * - if CookieScript is used, the pixels will be injected after the user accepted the "targeting" category
 * - if no CookieScript is used, the pixels will be injected immediately
 * @note if the environment is "development", the Meta Development Pixel (META_DEVELOPMENT_PIXEL value) will be used, so that no real data is sent to production pixels
 * @param pixels Comma separated list of Meta Pixel IDs or a single Pixel ID
 * @example
 * injectMetaPixels('1234567890,0987654321');
 * // or
 * injectMetaPixels('1234567890');
 * // or
 * injectMetaPixels('1234567890, 0987654321');
 */
export function injectMetaPixels(pixels: string) {
  if (process.env.NODE_ENV === 'development'){
    console.log("Using Meta Development Pixel:", META_DEVELOPMENT_PIXEL);
    pixels = META_DEVELOPMENT_PIXEL;
  }
  if (!window.CookieScript) {
    metaPixelCode(pixels);
    return;
  }
  window.addEventListener(OnAcceptedTargeting, () => {
    metaPixelCode(pixels);
  });
}

/**
 * ### Inject TikTok Pixels
 * - if CookieScript is used, the pixels will be injected after the user accepted the "targeting" category
 * - if no CookieScript is used, the pixels will be injected immediately
 * @note if the environment is "development", the TikTok Development Pixel (TIKTOK_DEVELOPMENT_PIXEL value) will be used, so that no real data is sent to production pixels
 * @param pixels Comma separated list of TikTok Pixel IDs or a single Pixel ID
 * @example
 * injectTikTokPixels('ABCD1234,EFGH5678');
 * // or
 * injectTikTokPixels('ABCD1234');
 * // or
 * injectTikTokPixels('ABCD1234, EFGH5678');
 *
 */
export function injectTikTokPixels(pixels: string) {
  if (process.env.NODE_ENV === 'development'){
    console.log("Using TikTok Development Pixel:", TIKTOK_DEVELOPMENT_PIXEL);
    pixels = TIKTOK_DEVELOPMENT_PIXEL;
  }
  if (!window.CookieScript) {
    tiktokPixelCode(pixels);
    return;
  }
  window.addEventListener(OnAcceptedTargeting, () => {
    tiktokPixelCode(pixels);
  });
}

function metaPixelCode(pixels: string){
  const pixelIds = Array.from(
    new Set(
      pixels
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
    )
  );
  if (pixelIds.length === 0) return;

  const initLines = pixelIds.map((id) => `fbq('init', '${id}');`).join('\n');

  const pixelScript = `
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    ${initLines}
    fbq('track', 'PageView');
  `;

  const scriptEl = document.createElement('script');
  scriptEl.innerHTML = pixelScript;
  document.head.appendChild(scriptEl);

  const noscriptHtml = pixelIds
    .map(
      (id) => `
        <img height="1" width="1" style="display:none"
             src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1"
        />
      `
    )
    .join('');

  const noscriptEl = document.createElement('noscript');
  noscriptEl.innerHTML = noscriptHtml;
  document.head.appendChild(noscriptEl);
}



function tiktokPixelCode(pixels: string){
  const pixelIds = Array.from(
    new Set(
      pixels
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
    )
  );
  if (pixelIds.length === 0) return;

  const pixelScript = `
    !function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = w[t] = w[t] || [];
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
      ttq.setAndDefer = function (t, e) {
        t[e] = function () {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      for (var i = 0; i < ttq.methods.length; i++) {
        ttq.setAndDefer(ttq, ttq.methods[i]);
      }
      ttq.instance = function (t) {
        for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) {
          ttq.setAndDefer(e, ttq.methods[n]);
        }
        return e;
      };
      ttq.load = function (e, n) {
        var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {};
        ttq._i[e] = [];
        ttq._i[e]._u = i;
        ttq._t = ttq._t || {};
        ttq._t[e] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[e] = n || {};
        var o = document.createElement("script");
        o.type = "text/javascript";
        o.async = !0;
        o.src = i + "?sdkid=" + e + "&lib=" + t;
        var a = document.getElementsByTagName("script")[0];
        a.parentNode.insertBefore(o, a);
      };

      ${pixelIds.map((id) => `ttq.load('${id}');`).join('\n')}
      ttq.page();
    }(window, document, 'ttq');
  `;
  const scriptEl = document.createElement('script');
  scriptEl.innerHTML = pixelScript;
  document.head.appendChild(scriptEl);
}

